import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { useUpdateTransaction } from '@/hooks/api'
import { editTransactionSchema, getEditDefaultValues } from './EditTransactionDialog.static'
import type { EditTransactionFormValues } from './EditTransactionDialog.static'
import type { FinancialTransaction, ApiError } from '@/types'

interface UseEditTransactionDialogOptions {
  transaction: FinancialTransaction
  onClose: () => void
}

export function useEditTransactionDialog({
  transaction,
  onClose,
}: UseEditTransactionDialogOptions) {
  const { t } = useTranslation('financials')
  const { t: tCommon } = useTranslation('common')

  const form = useForm<EditTransactionFormValues>({
    resolver: zodResolver(editTransactionSchema),
    defaultValues: getEditDefaultValues(transaction),
    mode: 'onBlur',
  })

  useEffect(() => {
    form.reset(getEditDefaultValues(transaction))
  }, [transaction, form])

  const updateMutation = useUpdateTransaction()
  const isLoading = updateMutation.isPending

  const onSubmit = (data: EditTransactionFormValues) => {
    const amountCents = Math.round(Number(data.amount) * 100)

    updateMutation.mutate(
      {
        id: transaction.id,
        data: {
          transactionType: data.transactionType,
          category: data.category,
          description: data.description,
          amountCents,
          transactionDate: data.transactionDate,
          patientId: data.patientId || null,
          paymentMethod: data.paymentMethod,
          referenceNumber: data.referenceNumber || null,
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
        onError: (error) => {
          const axiosError = error as AxiosError<ApiError>
          const message = axiosError.response?.data?.error?.message || t('form.errors.createFailed')
          form.setError('root', { type: 'manual', message })
        },
      }
    )
  }

  return {
    t,
    tCommon,
    form,
    isLoading,
    onSubmit,
  }
}
